/**
 * Slice 58 (owner side of Slice 57) — Unit tab · Photo groups.
 * Group items by where they live in a room. The cleaner gets one photo prompt per group, in this order,
 * in the Walk step only. Items with no group are covered by the whole-room photo.
 */
import { useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Camera, Check, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { prepareImageFileForUpload } from "@/lib/prepareImageForUpload";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import { useDeleteUnitItem, useUnitItems, useUpdateUnitItem } from "@/hooks/useUnitItems";
import { useInventoryItems, useDeleteInventoryItem } from "@/hooks/useInventory";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useAppliances, useDeleteAppliance } from "@/hooks/useAppliances";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/queryKeys";
import {
  useCreatePhotoGroup,
  useDeletePhotoGroup,
  usePhotoGroups,
  useUpdatePhotoGroup,
} from "@/hooks/usePhotoGroups";
import {
  defaultGroupPrompt,
  groupPrompt,
  groupsForRoom,
  itemLabel,
  sameRoom,
  type PhotoGroup,
} from "@/lib/photoGroups";
import { sortRoomsWalkOrder } from "@/lib/roomCoverPhotos";
import { sortUnitItems } from "@/lib/unitItems";

const SUGGESTIONS = [
  "Cutlery drawer",
  "Plates and bowls shelf",
  "Pots and pans cupboard",
  "Utensil drawer",
  "Under the sink",
  "Towel shelf",
  "Wardrobe",
  "Bedside table",
];

function SpotCard({
  group,
  index,
  total,
  itemNames,
  busy,
  onUploadRef,
  onRename,
  onRemove,
  onMove,
  onTakeOut,
}: {
  group: PhotoGroup;
  index: number;
  total: number;
  itemNames: { key: string; name: string }[];
  busy: boolean;
  onUploadRef: (file: File) => void;
  onRename: (name: string) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  /** Put one item back "In the open". */
  onTakeOut: (key: string) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(group.name);
  return (
    <div className="flex gap-3.5 rounded-[22px] bg-white p-4 max-sm:flex-col">
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onUploadRef(f);
          e.target.value = "";
        }}
      />
      <div className="flex items-start gap-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#e2eeed] text-[13px] font-bold text-[#15776d]">
          {index + 2}
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          className={cn(
            "relative flex h-[84px] w-[120px] shrink-0 items-center justify-center overflow-hidden rounded-[14px]",
            group.reference_photo_url ? "bg-[#eef2f5]" : "border-2 border-dashed border-[#cfdbe2] bg-[#f8fafb] text-[#15776d]",
          )}
        >
          {group.reference_photo_url ? (
            <img src={group.reference_photo_url} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex flex-col items-center gap-1 px-2 text-center text-[12.5px] font-bold">
              <Camera className="h-4 w-4" />
              Add photo later
            </span>
          )}
        </button>
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && name.trim() !== group.name && onRename(name.trim())}
            aria-label="Spot name"
            className="h-10 min-w-0 flex-1 rounded-[10px] border-[1.5px] border-transparent bg-transparent px-2 text-[17px] font-bold text-[#0f1720] outline-none hover:border-[#e6edf1] focus:border-[#24a89a]"
          />
          {!group.reference_photo_url ? (
            <span className="rounded-full bg-[#fdf3e5] px-2.5 py-1 text-[12px] font-bold text-[#9a5a00]">No photo yet</span>
          ) : null}
          <button
            type="button"
            onClick={() => onMove(-1)}
            disabled={index === 0}
            aria-label="Move up"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[#64748b] hover:bg-[#f1f5f8] disabled:opacity-30"
          >
            <ArrowUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => onMove(1)}
            disabled={index === total - 1}
            aria-label="Move down"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] text-[#64748b] hover:bg-[#f1f5f8] disabled:opacity-30"
          >
            <ArrowDown className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="h-9 shrink-0 rounded-[10px] px-2.5 text-[13px] font-bold text-[#b4453a] hover:bg-[#fdf1ef]"
          >
            Remove
          </button>
        </div>
        {itemNames.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {itemNames.map((it) => (
              <span key={it.key} className="group inline-flex items-center gap-1.5 rounded-full bg-[#f1f5f7] py-1.5 pl-3 pr-2 text-[13.5px] font-bold text-[#0f1720]">
                {it.name}
                <button
                  type="button"
                  onClick={() => onTakeOut(it.key)}
                  aria-label={`Put ${it.name} back in the open`}
                  className="flex h-5 w-5 items-center justify-center rounded-full text-[#8494a2] hover:bg-white hover:text-[#b4453a]"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        ) : (
          <div className="text-[13.5px] font-medium text-[#8a5a00]">No items yet. Tick items below and make a spot from them.</div>
        )}
        {!group.reference_photo_url ? (
          <div className="text-[12.5px] font-medium text-[#8494a2]">Photo optional, add it any time. Cleaners see the item photos until you do.</div>
        ) : null}
      </div>
    </div>
  );
}

export default function PhotoGroupsSection({ propertyId }: { propertyId: string }) {
  const { data: roomRows = [] } = usePropertyRooms(propertyId);
  const { data: cover = {} } = useRoomCoverData(propertyId);
  const { data: itemsRaw = [] } = useUnitItems(propertyId);
  const { data: groups = [] } = usePhotoGroups(propertyId);
  const createGroup = useCreatePhotoGroup();
  const updateGroup = useUpdatePhotoGroup();
  const deleteGroup = useDeletePhotoGroup();
  const updateItem = useUpdateUnitItem();
  const queryClient = useQueryClient();
  const { data: invRaw = [] } = useInventoryItems(propertyId);
  const { data: invRooms = [] } = useInventoryRooms(propertyId);
  const { data: applRaw = [] } = useAppliances(propertyId);
  const deleteUnit = useDeleteUnitItem();
  const deleteInv = useDeleteInventoryItem();
  const deleteAppl = useDeleteAppliance(propertyId);

  const rooms = useMemo(() => sortRoomsWalkOrder(roomRows.filter((r) => !r.archived_at), cover), [roomRows, cover]);
  const items = useMemo(() => sortUnitItems(itemsRaw), [itemsRaw]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Pick several items, then put them all in one spot (no photo needed).
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [bulkName, setBulkName] = useState("");
  const [addOpen, setAddOpen] = useState(false);

  const room = rooms.find((r) => r.id === selectedId) ?? rooms[0] ?? null;
  const roomGroups = room ? groupsForRoom(groups, room.id) : [];
  // Every kind of thing in the room can live in a spot: unit items, supplies and appliances.
  type SpotRow = {
    key: string;
    kind: "unit" | "supply" | "appliance";
    id: string;
    name: string;
    photo: string | null;
    quantity: number | null;
    groupId: string | null;
  };
  const allRows = (roomName: string): SpotRow[] => {
    const unitRows: SpotRow[] = items
      .filter((i) => sameRoom(i.room, roomName))
      .map((i) => ({ key: `u-${i.id}`, kind: "unit", id: i.id, name: i.name, photo: i.photo_url ?? null, quantity: i.quantity ?? null, groupId: i.photo_group_id ?? null }));
    const invRoomIds = new Set(invRooms.filter((r) => sameRoom(r.name, roomName)).map((r) => r.id));
    const supplyRows: SpotRow[] = (invRaw as { id: string; item_name?: string | null; photo_url?: string | null; room_id?: string | null; photo_group_id?: string | null }[])
      .filter((i) => i.room_id && invRoomIds.has(i.room_id))
      .map((i) => ({ key: `s-${i.id}`, kind: "supply", id: i.id, name: i.item_name || "Supply", photo: i.photo_url ?? null, quantity: null, groupId: i.photo_group_id ?? null }));
    const applianceRows: SpotRow[] = (applRaw as { id: string; name?: string | null; location?: string | null; photo_urls?: string[] | null; photo_group_id?: string | null }[])
      .filter((a) => sameRoom(a.location || "", roomName))
      .map((a) => ({ key: `a-${a.id}`, kind: "appliance", id: a.id, name: a.name || "Appliance", photo: a.photo_urls?.[0] ?? null, quantity: null, groupId: a.photo_group_id ?? null }));
    return [...unitRows, ...supplyRows, ...applianceRows];
  };
  const roomItems = room ? allRows(room.name) : [];
  const groupIds = new Set(roomGroups.map((g) => g.id));

  const moveRow = async (row: SpotRow, groupId: string | null) => {
    if (row.kind === "unit") {
      await updateItem.mutateAsync({ id: row.id, propertyId, photoGroupId: groupId });
      return;
    }
    const table = row.kind === "supply" ? "inventory_items" : "appliances";
    const { error } = await supabase.from(table).update({ photo_group_id: groupId } as never).eq("id", row.id);
    if (error) throw error;
    await queryClient.invalidateQueries({ queryKey: row.kind === "supply" ? queryKeys.inventory.byProperty(propertyId) : queryKeys.maintenance.appliances(propertyId) });
  };
  const removeRow = async (row: SpotRow) => {
    if (!window.confirm(`Delete ${row.name}? Its photo goes too.`)) return;
    if (row.kind === "unit") await deleteUnit.mutateAsync({ id: row.id, propertyId });
    else if (row.kind === "supply") await deleteInv.mutateAsync({ id: row.id, propertyId });
    else await deleteAppl.mutateAsync(row.id);
  };

  if (rooms.length === 0) return null;

  const upload = async (file: File) => {
    const prepared = await prepareImageFileForUpload(file);
    const path = `${propertyId}/photo-groups/${crypto.randomUUID()}.jpg`;
    const { error } = await supabase.storage.from("reference-photos").upload(path, prepared, {
      contentType: "image/jpeg",
      upsert: false,
    });
    if (error) throw error;
    return supabase.storage.from("reference-photos").getPublicUrl(path).data.publicUrl;
  };

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast({
        title: "Couldn’t save",
        // Supabase errors are plain objects, not Error instances, so read the message directly.
        description: (err as { message?: string } | null)?.message || undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const toggleSel = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const moveSelected = (groupId: string | null) =>
    void run(async () => {
      const rows = roomItems.filter((r) => selected.has(r.key));
      for (const r of rows) await moveRow(r, groupId);
      setSelected(new Set());
    });

  const move = (g: PhotoGroup, dir: -1 | 1) => {
    const i = roomGroups.findIndex((x) => x.id === g.id);
    const other = roomGroups[i + dir];
    if (!other) return;
    void run(async () => {
      // Re-number the whole room so the order is always unambiguous.
      const order = [...roomGroups];
      order[i] = other;
      order[i + dir] = g;
      await Promise.all(
        order.map((x, idx) =>
          x.sort_order === idx ? Promise.resolve() : updateGroup.mutateAsync({ id: x.id, propertyId, sortOrder: idx }),
        ),
      );
    });
  };

  const usedNames = new Set(roomGroups.map((g) => g.name.toLowerCase()));
  const suggestions = SUGGESTIONS.filter((n) => !usedNames.has(n.toLowerCase())).slice(0, 4);
  const loose = roomItems.filter((i) => !i.groupId || !groupIds.has(i.groupId));
  const showBar = selected.size > 0 || addOpen;

  /** Make a spot from whatever is ticked (possibly nothing: an empty spot to fill later). */
  const makeSpot = (raw: string) => {
    const name = raw.trim();
    if (!name || !room) return;
    void run(async () => {
      const made = await createGroup.mutateAsync({
        propertyId,
        roomId: room.id,
        name,
        prompt: defaultGroupPrompt(name),
        sortOrder: roomGroups.length ? Math.max(...roomGroups.map((g) => g.sort_order)) + 1 : 0,
      });
      for (const r of roomItems.filter((x) => selected.has(x.key))) await moveRow(r, made.id);
      setSelected(new Set());
      setBulkName("");
      setAddOpen(false);
    });
  };
  const takeOut = (key: string) => {
    const row = roomItems.find((r) => r.key === key);
    if (row) void run(() => moveRow(row, null));
  };
  const prompt = (
    <div className="flex items-center gap-2.5 rounded-[16px] bg-[#e2eeed] px-4 py-3 text-[14.5px] font-bold text-[#15776d]">
      <Check className="h-4 w-4" strokeWidth={2.6} />
      Tick what lives together, then name the spot
    </div>
  );

  return (
    <div className="flex flex-col gap-4" data-tour="tour-unit-photo-groups">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex max-w-[640px] flex-col gap-1">
          <div className="text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">Spots</div>
          <div className="text-[14px] font-medium leading-snug text-[#64748b]">
            Where things live. Cleaners photograph them in this order.
          </div>
        </div>
        <button
          type="button"
          onClick={() => setAddOpen((v) => !v)}
          className="flex h-11 shrink-0 items-center gap-2 rounded-[14px] bg-[#15776d] px-4 text-[14.5px] font-bold text-white"
        >
          <Camera className="h-4 w-4" />
          Add spot
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {rooms.map((r) => {
          const n = groupsForRoom(groups, r.id).length;
          const on = room?.id === r.id;
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => {
                setSelectedId(r.id);
                setSelected(new Set());
                setAddOpen(false);
              }}
              className={cn(
                "flex h-10 items-center gap-2 rounded-full px-3.5 text-[14px] font-bold",
                on ? "bg-[#15776d] text-white" : "bg-white text-[#0f1720] hover:bg-[#f1f5f8]",
              )}
            >
              {r.name}
              <span className={cn("text-[12.5px] font-bold", on ? "text-[#bfe3dd]" : n ? "text-[#15776d]" : "text-[#8494a2]")}>
                {n ? `${n} spot${n > 1 ? "s" : ""}` : "No spots"}
              </span>
            </button>
          );
        })}
      </div>

      {room ? (
        <div className="flex flex-col gap-3">
          {roomGroups.length === 0 && loose.length > 0 ? prompt : null}

          {roomGroups.map((g, i) => (
            <SpotCard
              key={g.id}
              group={g}
              index={i}
              total={roomGroups.length}
              itemNames={roomItems.filter((it) => it.groupId === g.id).map((it) => ({ key: it.key, name: it.name }))}
              busy={busy}
              onUploadRef={(file) =>
                void run(async () => {
                  const url = await upload(file);
                  await updateGroup.mutateAsync({ id: g.id, propertyId, referencePhotoUrl: url });
                })
              }
              onRename={(name) => void run(() => updateGroup.mutateAsync({ id: g.id, propertyId, name }))}
              onRemove={() => void run(() => deleteGroup.mutateAsync({ id: g.id, propertyId }))}
              onMove={(dir) => move(g, dir)}
              onTakeOut={takeOut}
            />
          ))}

          {roomGroups.length > 0 && loose.length > 0 ? prompt : null}

          <div className="overflow-hidden rounded-[22px] bg-white">
            <div className="flex flex-wrap items-center gap-3 px-4 py-3.5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-[12px] bg-[#eef2f5] text-[10.5px] font-bold text-[#8494a2]">
                {cover[room.id]?.cover_photo_url ? (
                  <img src={cover[room.id]!.cover_photo_url!} alt="" className="h-full w-full object-cover" />
                ) : (
                  "room"
                )}
              </div>
              <div className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">In the open</div>
              <div className="text-[13px] font-medium text-[#8494a2]">
                {loose.length} item{loose.length === 1 ? "" : "s"}
              </div>
              <span className="ml-auto rounded-full bg-[#eef2f5] px-2.5 py-1 text-[11.5px] font-bold text-[#475569]">Always first</span>
            </div>
            {loose.length === 0 ? (
              <div className="border-t-[1.5px] border-[#eef2f5] px-4 py-5 text-[14px] font-medium text-[#8494a2]">
                {roomItems.length === 0 ? "Nothing in this room yet. Add items with the camera walk." : "Every item is in a spot."}
              </div>
            ) : (
              <div className="grid border-t-[1.5px] border-[#eef2f5] md:grid-cols-2">
                {loose.map((it) => (
                  <label
                    key={it.key}
                    className="flex cursor-pointer items-center gap-3 border-b border-[#eef2f5] px-4 py-2.5 hover:bg-[#fafcfc]"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(it.key)}
                      onChange={() => toggleSel(it.key)}
                      aria-label={`Select ${it.name}`}
                      className="h-5 w-5 shrink-0 accent-[#15776d]"
                    />
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-[10px] bg-[#eff4f7]">
                      {it.photo ? <img src={it.photo} alt="" className="h-full w-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0 flex-1 truncate text-[15px] font-bold text-[#0f1720]">{itemLabel(it.name, it.quantity)}</div>
                    <div className="shrink-0 text-[12.5px] font-bold text-[#8494a2]">
                      {it.kind === "unit" ? "Unit item" : it.kind === "supply" ? "Supply" : "Appliance"}
                    </div>
                    <button
                      type="button"
                      aria-label={`Delete ${it.name}`}
                      onClick={(e) => {
                        e.preventDefault();
                        void run(() => removeRow(it));
                      }}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[16px] font-bold text-[#c3ccd2] hover:bg-[#fdf1ef] hover:text-[#b4453a]"
                    >
                      ×
                    </button>
                  </label>
                ))}
              </div>
            )}
          </div>

          {showBar ? (
            <div className="sticky bottom-3 z-10 flex flex-col gap-3 rounded-[20px] bg-white p-3.5 shadow-[0_12px_34px_rgba(15,23,32,0.18)]">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="flex h-10 shrink-0 items-center gap-2 rounded-full bg-[#e2eeed] pl-1.5 pr-3.5 text-[13.5px] font-bold text-[#15776d]">
                  <span className="flex h-7 min-w-7 items-center justify-center rounded-full bg-[#15776d] px-1.5 text-[13px] text-white">{selected.size}</span>
                  ticked
                </span>
                <input
                  value={bulkName}
                  onChange={(e) => setBulkName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && makeSpot(bulkName)}
                  placeholder="Name the spot, e.g. Cutlery drawer"
                  aria-label="Spot name"
                  className="h-11 min-w-0 flex-1 rounded-[12px] border-[1.5px] border-[#e6edf1] bg-[#f8fafb] px-3.5 text-[15px] font-bold outline-none focus:border-[#24a89a]"
                />
                <button
                  type="button"
                  disabled={!bulkName.trim() || busy}
                  onClick={() => makeSpot(bulkName)}
                  className={cn(
                    "flex h-11 shrink-0 items-center gap-1.5 rounded-[12px] px-4 text-[14.5px] font-bold",
                    bulkName.trim() ? "bg-[#15776d] text-white" : "bg-[#d5e3e0] text-white",
                  )}
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "+ Make spot"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(new Set());
                    setAddOpen(false);
                    setBulkName("");
                  }}
                  className="h-11 shrink-0 px-2 text-[14px] font-bold text-[#64748b]"
                >
                  Clear
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setBulkName(n)}
                    className={cn(
                      "h-9 rounded-full px-3.5 text-[13.5px] font-bold",
                      bulkName === n ? "bg-[#15776d] text-white" : "bg-[#f1f5f8] text-[#475569] hover:bg-[#e6edf1]",
                    )}
                  >
                    {n}
                  </button>
                ))}
              </div>
              {roomGroups.length > 0 && selected.size > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-medium text-[#64748b]">Or move them into a saved spot</span>
                  {roomGroups.map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      disabled={busy}
                      onClick={() => moveSelected(g.id)}
                      className="flex h-10 items-center gap-2 rounded-[12px] bg-[#f4f7f9] pl-1.5 pr-3 text-[13.5px] font-bold text-[#0f1720] hover:bg-[#e6edf1]"
                    >
                      <span className="h-7 w-7 shrink-0 overflow-hidden rounded-[8px] bg-[#e6edf1]">
                        {g.reference_photo_url ? <img src={g.reference_photo_url} alt="" className="h-full w-full object-cover" /> : null}
                      </span>
                      {g.name} →
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
